import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { Track } from 'livekit-client';

/**
 * Renders video + audio tracks for a single LiveKit participant.
 * Works for both local and remote participants.
 */
export default function VideoTile({ participant, label, mirror = false, primary = false }) {
  const videoRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!participant) return;

    const videoEl = videoRef.current;
    const audioEl = audioRef.current;

    const attached = [];

    const attachTracks = () => {
      const videoPub = participant.getTrackPublication?.(Track.Source.Camera)
        || Array.from(participant.trackPublications?.values?.() || [])
            .find(pub => pub.kind === Track.Kind.Video);

      if (videoPub?.track && videoEl) {
        videoPub.track.attach(videoEl);
        attached.push(videoPub.track);
      }

      // Never play back the local participant's own microphone — it would
      // cause self-echo. Remote audio only.
      if (!mirror) {
        const audioPub = participant.getTrackPublication?.(Track.Source.Microphone)
          || Array.from(participant.trackPublications?.values?.() || [])
              .find(pub => pub.kind === Track.Kind.Audio);

        if (audioPub?.track && audioEl) {
          audioPub.track.attach(audioEl);
          attached.push(audioPub.track);
        }
      }
    };

    attachTracks();

    const rerun = () => attachTracks();
    participant.on?.('trackSubscribed', rerun);
    participant.on?.('trackPublished', rerun);
    participant.on?.('localTrackPublished', rerun);

    return () => {
      participant.off?.('trackSubscribed', rerun);
      participant.off?.('trackPublished', rerun);
      participant.off?.('localTrackPublished', rerun);
      attached.forEach(t => {
        try { t.detach(); } catch (_) { /* noop */ }
      });
    };
  }, [participant]);

  // If the label looks like an email ("foo@bar.com"), render just the local part.
  const prettyLabel = typeof label === 'string' && label.includes('@')
    ? label.split('@')[0]
    : label;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        aspectRatio: primary ? '16 / 9' : '4 / 3',
        bgcolor: 'grey.900',
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: primary ? 3 : 1
      }}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={mirror}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          transform: mirror ? 'scaleX(-1)' : 'none',
          display: 'block'
        }}
      />
      <audio ref={audioRef} autoPlay playsInline muted={mirror} />
      {prettyLabel && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            left: 8,
            px: 1,
            py: 0.25,
            maxWidth: 'calc(100% - 16px)',
            bgcolor: 'rgba(0,0,0,0.6)',
            color: 'common.white',
            borderRadius: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          <Typography variant="caption">{prettyLabel}</Typography>
        </Box>
      )}
    </Box>
  );
}
